import { UserPreferences } from "@coworker/types/lib/userProfileTypes";

type CwhDataType =
  "string" | "boolean" | "integer" | "number" | "object" | "array";

interface CwhUserPreferenceInput {
  key: string;
  value: string | boolean | number;
  description: string;
  dataType: CwhDataType;
}

interface CwhUserPreference {
  key: string;
  value: string | boolean | number;
  description?: string;
  dataType: CwhDataType | string;
}

export function toUserPreferenceList(data: unknown): CwhUserPreference[] {
  const list: unknown[] = Array.isArray(data)
    ? data
    : data &&
        typeof data === "object" &&
        Array.isArray((data as { userPreferences?: unknown }).userPreferences)
      ? (data as { userPreferences: unknown[] }).userPreferences
      : [];

  return list.filter((item): item is CwhUserPreference => {
    if (!item || typeof item !== "object") return false;

    const preference = item as Record<string, unknown>;
    return (
      typeof preference["key"] === "string" &&
      typeof preference["dataType"] === "string" &&
      "value" in preference &&
      (typeof preference["value"] === "string" ||
        typeof preference["value"] === "boolean" ||
        typeof preference["value"] === "number")
    );
  });
}

export function parsePreferenceValue(
  value: unknown,
  dataType: string
): unknown {
  if (typeof value !== "string") return value;

  switch (dataType.toLowerCase()) {
    case "boolean":
      return value.toLowerCase() === "true";
    case "integer":
    case "number": {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }
    case "object":
      try {
        return JSON.parse(value) as unknown;
      } catch {
        return value;
      }
    case "array":
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    case "string":
    default:
      return value;
  }
}

export function mapCWHPreferencesForFrontend(data: unknown): UserPreferences {
  const preferences: Record<string, unknown> = {};
  for (const entry of toUserPreferenceList(data)) {
    preferences[entry.key] = parsePreferenceValue(entry.value, entry.dataType);
  }
  return preferences as UserPreferences;
}

export function toCwhUserPreferences(
  update: Record<string, unknown>
): CwhUserPreferenceInput[] {
  return Object.entries(update).map(([key, value]) => {
    if (typeof value === "boolean") {
      return { key, value, description: key, dataType: "boolean" };
    }
    if (typeof value === "number") {
      return { key, value, description: key, dataType: "number" };
    }
    if (typeof value === "string") {
      return { key, value, description: key, dataType: "string" };
    }
    let serializedValue: string | undefined;
    try {
      serializedValue = JSON.stringify(value);
    } catch {
      throw new Error(`Preference "${key}" cannot be serialized for CWH`);
    }
    if (serializedValue === undefined) {
      throw new Error(`Preference "${key}" cannot be serialized for CWH`);
    }
    return {
      key,
      value: serializedValue,
      description: key,
      dataType: "object",
    };
  });
}
